import { useEffect, useRef, useState, useCallback } from "react";
import Phaser from "phaser";
import GardenScene, { WORLD_W, WORLD_H } from "../game/GardenScene";
import audio from "../game/audio";
import ShareModal from "./ShareModal";
import RewardBox from "./RewardBox";
import { Fan, Volume2, VolumeX } from "lucide-react";

const REWARD_KEY = "austen_reward_v1";

const STORAGE_KEY = "austen_quest_v1";

const DIALOGUES = {
  intro: {
    name: "Elizabeth Bennet",
    text:
      "Avrei bisogno del vostro aiuto. Il mio ventaglio è scomparso e affrontare una sala piena di persone senza la possibilità di nascondere un sorriso sarebbe estremamente imprudente.",
    action: "Accetto l'incarico",
    next: "active",
  },
  reminder: {
    name: "Elizabeth Bennet",
    text:
      "Vi prego, cercate ancora. Credo di aver smarrito il mio ventaglio nell'angolo del giardino, tra i cespugli di rose.",
    action: "Vado subito",
    next: null,
  },
  deliver: {
    name: "Elizabeth Bennet",
    text: "Eccolo! Avete salvato il mio ventaglio e, cosa ancora più rara, la mia pazienza.",
    action: "Con piacere",
    next: "completed",
  },
  done: {
    name: "Elizabeth Bennet",
    text: "Vi sarò per sempre grata. Godetevi la quiete di questo giardino.",
    action: "Chiudi",
    next: null,
  },
};

// Optional, branching conversation with Mr Darcy. Purely flavour: it never
// starts quests, gives items or touches the inventory / fan mission.
const DARCY_INTRO = {
  text:
    "Temo di non essere particolarmente abile nelle conversazioni con persone che non conosco. Tuttavia, sarebbe scortese non darvi il benvenuto a Pemberley.",
  choices: [
    {
      label: "La vostra accoglienza è più gentile di quanto mi avessero lasciato immaginare.",
      reply: "Le prime impressioni, come ho avuto modo di apprendere, non sono sempre le più affidabili.",
    },
    {
      label: "Non temete: neppure io sono qui per pronunciare un discorso.",
      reply: "Ne sono sollevato. I discorsi troppo lunghi dovrebbero essere riservati a Mr Collins.",
    },
  ],
  conclude: "Vi auguro una piacevole passeggiata. Il giardino è particolarmente bello a quest'ora.",
};

const DARCY_FOLLOWUPS = [
  {
    text: "Avete trovato Pemberley di vostro gradimento?",
    choices: [
      {
        label: "È difficile trovare qualcosa da criticare.",
        reply: "Elizabeth direbbe che non avete osservato abbastanza attentamente.",
      },
      {
        label: "Sto ancora esplorando.",
        reply: "Allora non permetterò alla conversazione di trattenervi.",
      },
    ],
  },
  {
    text: "Mi pare che Miss Bennet vi abbia affidato una questione importante.",
    choices: [
      {
        label: "Sto cercando il suo ventaglio.",
        reply: "Spero che lo troviate. Elizabeth tiene molto agli oggetti che le permettono di nascondere un sorriso.",
      },
      {
        label: "La discrezione mi impedisce di parlarne.",
        reply: "Una risposta ammirevole. E, devo ammetterlo, alquanto sospetta.",
      },
    ],
  },
  {
    text: "Non vorrei interrompere le vostre indagini.",
    choices: [
      {
        label: "Una breve conversazione non farà alcun danno.",
        reply: "Una breve conversazione è precisamente l'unica nella quale possa promettere di distinguermi.",
      },
      {
        label: "In effetti, il tempo stringe.",
        reply: "Naturalmente. Non vi tratterrò oltre.",
      },
    ],
  },
];

export default function AustenGame() {
  const parentRef = useRef(null);
  const gameRef = useRef(null);
  const busRef = useRef(null);
  const controlsRef = useRef({ x: 0, y: 0 });

  const [quest, setQuest] = useState(() => {
    // Safe migration: accept only known states, otherwise reset ONLY the
    // mission (audio/other prefs are untouched). Guards against incoherent
    // saves left by earlier versions.
    const v = localStorage.getItem(STORAGE_KEY);
    const valid = ["not_started", "active", "has_fan", "completed"];
    if (valid.includes(v)) return v;
    try { localStorage.setItem(STORAGE_KEY, "not_started"); } catch {}
    return "not_started";
  });
  const questRef = useRef(quest);
  const [near, setNear] = useState({ nearEliza: false, nearDarcy: false, nearFan: false });
  const [dialogue, setDialogue] = useState(null); // key of DIALOGUES (Elizabeth)
  const [darcyConvo, setDarcyConvo] = useState(null); // { base, step, isIntro, reply }
  const [darcyMet, setDarcyMet] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [confirmRestart, setConfirmRestart] = useState(false);
  const [portrait, setPortrait] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [muted, setMuted] = useState(() => audio.isMuted());
  const [needAudioTap, setNeedAudioTap] = useState(false);

  const persist = useCallback((q) => {
    setQuest(q);
    questRef.current = q;
    localStorage.setItem(STORAGE_KEY, q);
  }, []);

  // orientation watcher
  useEffect(() => {
    const check = () => setPortrait(window.innerHeight > window.innerWidth);
    check();
    window.addEventListener("resize", check);
    window.addEventListener("orientationchange", check);
    return () => {
      window.removeEventListener("resize", check);
      window.removeEventListener("orientationchange", check);
    };
  }, []);

  // Safety net: never let a Web Audio error surface as a red runtime overlay.
  // The game has no sound, but browsers / other scripts can still throw
  // "Cannot suspend/resume a closed AudioContext". These are harmless here.
  useEffect(() => {
    const isAudioErr = (msg) =>
      typeof msg === "string" &&
      /AudioContext/i.test(msg) &&
      /(closed|suspend|resume|start|not allowed|user gesture)/i.test(msg);
    const onError = (e) => {
      const msg = e?.message || e?.error?.message || "";
      if (isAudioErr(msg)) {
        e.stopImmediatePropagation?.();
        e.preventDefault?.();
        return false;
      }
    };
    const onRejection = (e) => {
      const r = e?.reason;
      const msg = typeof r === "string" ? r : r?.message || "";
      if (isAudioErr(msg)) {
        e.stopImmediatePropagation?.();
        e.preventDefault?.();
        return false;
      }
    };
    window.addEventListener("error", onError, true);
    window.addEventListener("unhandledrejection", onRejection, true);
    return () => {
      window.removeEventListener("error", onError, true);
      window.removeEventListener("unhandledrejection", onRejection, true);
    };
  }, []);

  // Audio is unlocked from the menu gesture (ATTIVA AUDIO E GIOCA). Here we only
  // re-arm it on mount if the user did NOT choose "gioca senza audio".
  useEffect(() => {
    if (!audio.isMuted()) audio.unlock();
    audio.onNeedGesture = () => setNeedAudioTap(true);
    return () => { if (audio.onNeedGesture) audio.onNeedGesture = null; };
  }, []);

  // Freeze player movement whenever a dialogue, the reward or victory screen is open.
  useEffect(() => {
    controlsRef.current.frozen =
      !!dialogue || !!darcyConvo || showComplete || showVictory || confirmRestart;
  }, [dialogue, darcyConvo, showComplete, showVictory, confirmRestart]);

  // Optional, non-blocking portrait hint (shown briefly, once).
  const hintShownRef = useRef(false);
  useEffect(() => {
    if (portrait && !hintShownRef.current) {
      hintShownRef.current = true;
      setShowHint(true);
      const t = setTimeout(() => setShowHint(false), 6000);
      return () => clearTimeout(t);
    }
  }, [portrait]);

  // Phaser boot
  useEffect(() => {
    const bus = new Phaser.Events.EventEmitter();
    busRef.current = bus;

    const game = new Phaser.Game({
      type: Phaser.AUTO,
      parent: parentRef.current,
      backgroundColor: "#7fae57",
      scale: {
        mode: Phaser.Scale.RESIZE,
        width: "100%",
        height: "100%",
      },
      physics: { default: "arcade", arcade: { gravity: { y: 0 }, debug: false } },
      // The game uses no sound; disabling Web Audio prevents Phaser from
      // creating an AudioContext and calling resume()/suspend() on it after
      // it is closed (on destroy / scene restart / tab visibility change),
      // which caused intermittent "Cannot suspend/resume a closed AudioContext".
      audio: { noAudio: true },
      scene: [GardenScene],
      callbacks: {
        postBoot: (g) => {
          g.registry.set("controls", controlsRef.current);
          g.registry.set("bus", bus);
          g.registry.set("quest", questRef.current);
        },
      },
    });
    gameRef.current = game;

    bus.on("proximity", (p) => setNear(p));
    bus.on("picked", () => { persist("has_fan"); bus.emit("quest", "has_fan"); audio.pickup(); });
    // Whenever the Phaser scene (re)creates, push the current mission state so
    // it never runs with a stale quest (fixes fan missing after restart/reload).
    bus.on("scene-ready", () => bus.emit("quest", questRef.current));

    return () => {
      bus.removeAllListeners();
      game.destroy(true);
      gameRef.current = null;
    };
  }, [persist]);

  // Action button logic
  let actionLabel = null;
  let actionKind = null;
  if (!dialogue && !darcyConvo) {
    if (near.nearEliza) {
      if (quest === "not_started") { actionLabel = "Parla"; actionKind = "talk-intro"; }
      else if (quest === "active") { actionLabel = "Parla"; actionKind = "talk-reminder"; }
      else if (quest === "has_fan") { actionLabel = "Consegna il ventaglio"; actionKind = "deliver"; }
      else if (quest === "completed") { actionLabel = "Parla"; actionKind = "talk-done"; }
    } else if (near.nearFan && quest === "active") {
      actionLabel = "Raccogli il ventaglio"; actionKind = "pickup";
    } else if (near.nearDarcy) {
      actionLabel = "Parla"; actionKind = "talk-darcy";
    }
  }

  const onAction = () => {
    if (actionKind === "talk-intro") { audio.dialogue(); setDialogue("intro"); }
    else if (actionKind === "talk-reminder") { audio.dialogue(); setDialogue("reminder"); }
    else if (actionKind === "talk-done") { audio.dialogue(); setDialogue("done"); }
    else if (actionKind === "deliver") { audio.dialogue(); setDialogue("deliver"); }
    else if (actionKind === "pickup") busRef.current?.emit("pickup");
    else if (actionKind === "talk-darcy") openDarcy();
  };

  const closeDialogue = () => {
    const d = DIALOGUES[dialogue];
    setDialogue(null);
    if (d?.next) {
      persist(d.next);
      busRef.current?.emit("quest", d.next);
      if (d.next === "completed") {
        audio.success();
        try { localStorage.setItem(REWARD_KEY, "1"); } catch {}
        // brief beat after the final line, then reveal the ending screen
        setTimeout(() => setShowVictory(true), 400);
      }
    }
  };

  // ---- Optional Mr Darcy conversation ----
  const openDarcy = () => {
    if (dialogue || darcyConvo) return; // never open two dialogues at once
    audio.dialogue();
    if (!darcyMet) {
      setDarcyConvo({ base: DARCY_INTRO, step: "ask", isIntro: true, reply: null });
    } else {
      const f = DARCY_FOLLOWUPS[Math.floor(Math.random() * DARCY_FOLLOWUPS.length)];
      setDarcyConvo({ base: f, step: "ask", isIntro: false, reply: null });
    }
  };
  const chooseDarcy = (i) => {
    audio.dialogue();
    setDarcyConvo((c) => ({ ...c, step: "reply", reply: c.base.choices[i].reply }));
  };
  const advanceDarcy = () => {
    if (darcyConvo?.step === "reply" && darcyConvo.isIntro) {
      setDarcyConvo((c) => ({ ...c, step: "bye" }));
    } else {
      setDarcyConvo(null);
      setDarcyMet(true);
    }
  };

  const toggleMute = () => {
    if (audio.isMuted()) {
      audio.setMuted(false);
      audio.unlock(); // this click is a gesture: resume + start music
      setNeedAudioTap(false);
    } else {
      audio.setMuted(true);
    }
    setMuted(audio.isMuted());
  };

  const playAgain = () => {
    setConfirmRestart(false);
    setShowVictory(false);
    setShareOpen(false);
    resetQuest(); // restart mission only: fan, inventory, dialogues, position
  };

  const resetQuest = () => {
    persist("not_started");
    busRef.current?.emit("quest", "not_started");
    // reload scene to respawn fan state cleanly
    const scene = gameRef.current?.scene.getScene("Garden");
    scene?.scene.restart();
    setTimeout(() => busRef.current?.emit("quest", "not_started"), 60);
  };

  const hasFan = quest === "has_fan" || quest === "completed";
  const dObj = dialogue ? DIALOGUES[dialogue] : null;

  return (
    <div className="austen-root" data-testid="austen-game-root">
      <div ref={parentRef} className="austen-canvas" data-testid="game-canvas" />

      {/* Quest banner */}
      <div className="hud-top" data-testid="quest-banner">
        <div className="quest-chip">
          <span className="quest-title">Missione</span>
          <span className="quest-text" data-testid="quest-status-text">
            {quest === "not_started" && "Parla con Elizabeth"}
            {quest === "active" && "Ritrova il ventaglio smarrito"}
            {quest === "has_fan" && "Riporta il ventaglio a Elizabeth"}
            {quest === "completed" && "Missione completata ✓"}
          </span>
        </div>
        <div className="hud-buttons">
          <button className="icon-btn" onClick={toggleMute} data-testid="mute-btn" title="Audio">
            {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
          </button>
          {process.env.NODE_ENV !== "production" && (
            <button className="reset-btn" onClick={resetQuest} data-testid="reset-quest-btn" title="Azzera missione">
              ↺
            </button>
          )}
        </div>
      </div>

      {/* Inventory */}
      <div className="inventory" data-testid="inventory">
        <div className={`inv-slot ${hasFan ? "filled" : ""}`} data-testid="inventory-slot-fan">
          {hasFan ? (
            <img src="/assets/fan.png" alt="ventaglio" data-testid="inventory-fan-icon" />
          ) : (
            <Fan size={26} className="inv-empty-icon" />
          )}
        </div>
        <span className="inv-label">{hasFan ? "Ventaglio" : "Vuoto"}</span>
      </div>

      {/* Action button */}
      {actionLabel && (
        <button className="action-btn" onClick={onAction} data-testid="action-button">
          {actionLabel}
        </button>
      )}

      {/* Dialogue */}
      {dObj && (
        <div className="dialogue-overlay" data-testid="dialogue-overlay">
          <div className="dialogue-box">
            <div className="dialogue-portrait">
              <img src="/assets/eliza_portrait.png" alt="Elizabeth" data-testid="dialogue-portrait" />
            </div>
            <div className="dialogue-content">
              <div className="dialogue-name" data-testid="dialogue-name">{dObj.name}</div>
              <p className="dialogue-text" data-testid="dialogue-text">{dObj.text}</p>
              <button className="dialogue-btn" onClick={closeDialogue} data-testid="dialogue-continue-btn">
                {dObj.action}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mission complete toast */}
      {showComplete && (
        <div className="complete-toast" data-testid="mission-complete">
          <span className="complete-emblem">✦</span>
          Missione completata!
        </div>
      )}

      {/* Victory / ending screen */}
      {showVictory && (
        <div className="victory-overlay" data-testid="victory-screen">
          <div className="victory-petals" aria-hidden="true">
            {Array.from({ length: 14 }).map((_, i) => (
              <span key={i} className={`petal p${i % 7}`} />
            ))}
          </div>
          <div className="victory-card">
            <h1 className="victory-title" data-testid="victory-title">MISSIONE COMPIUTA!</h1>
            <p className="victory-sub">
              Avete ritrovato il ventaglio di Elizabeth e conquistato la gratitudine di Pemberley.
            </p>
            <p className="victory-first">Questa era soltanto la prima avventura.</p>
            <p className="victory-body">
              Aiutateci a realizzare il videogioco completo: condividete questa demo{" "}
              <span className="hl-ovunque">OVUNQUE</span>. Più lettori entreranno nel mondo di
              Jane Austen, più grande potremo renderlo.
            </p>

            <div className="victory-chars">
              <div className="vchar">
                <img src="/assets/eliza_portrait.png" alt="Elizabeth" />
                <p className="vquote">
                  <b>Elizabeth</b> — Avete dimostrato ingegno, pazienza e una notevole capacità di
                  ritrovare ciò che gli altri smarriscono. Spero che tornerete presto.
                </p>
              </div>
              <div className="vchar">
                <img src="/assets/darcy_portrait.png" alt="Mr Darcy" />
                <p className="vquote">
                  <b>Mr Darcy</b> — Confesso che Pemberley sarebbe lieta di ricevervi nuovamente.
                </p>
              </div>
            </div>

            <RewardBox />

            <div className="victory-buttons">
              <button className="menu-btn wine" onClick={() => setShareOpen(true)} data-testid="victory-share-btn">
                CONDIVIDI OVUNQUE
              </button>
              <button className="menu-btn gold" onClick={() => setConfirmRestart(true)} data-testid="play-again-btn">
                GIOCA DI NUOVO
              </button>
              <button className="menu-btn primary" onClick={() => setShowVictory(false)} data-testid="continue-explore-btn">
                CONTINUA A ESPLORARE
              </button>
            </div>

            <div className="victory-foot">Land Editore</div>
          </div>

          {confirmRestart && (
            <div className="confirm-overlay" data-testid="confirm-restart" onClick={() => setConfirmRestart(false)}>
              <div className="confirm-card" onClick={(e) => e.stopPropagation()}>
                <p>Vuoi rigiocare la missione dall'inizio? Il vostro premio resterà al sicuro.</p>
                <div className="confirm-actions">
                  <button className="menu-btn primary" onClick={playAgain} data-testid="confirm-restart-yes">Sì, ricomincia</button>
                  <button className="menu-btn wine" onClick={() => setConfirmRestart(false)} data-testid="confirm-restart-no">Annulla</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} />

      {/* Mr Darcy optional conversation */}
      {darcyConvo && (
        <div className="dialogue-overlay" data-testid="darcy-dialogue-overlay">
          <div className="dialogue-box">
            <div className="dialogue-portrait darcy">
              <img src="/assets/darcy_portrait.png" alt="Mr Darcy" data-testid="darcy-portrait" />
            </div>
            <div className="dialogue-content">
              <div className="dialogue-name" data-testid="darcy-name">Mr Darcy</div>
              {darcyConvo.step === "ask" && (
                <>
                  <p className="dialogue-text" data-testid="darcy-text">{darcyConvo.base.text}</p>
                  <div className="dialogue-choices">
                    {darcyConvo.base.choices.map((ch, i) => (
                      <button
                        key={i}
                        className="choice-btn"
                        data-testid={`darcy-choice-${i}`}
                        onClick={() => chooseDarcy(i)}
                      >
                        {ch.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
              {darcyConvo.step === "reply" && (
                <>
                  <p className="dialogue-text" data-testid="darcy-text">{darcyConvo.reply}</p>
                  <button className="dialogue-btn" data-testid="darcy-continue-btn" onClick={advanceDarcy}>
                    {darcyConvo.isIntro ? "Continua" : "Addio"}
                  </button>
                </>
              )}
              {darcyConvo.step === "bye" && (
                <>
                  <p className="dialogue-text" data-testid="darcy-text">{DARCY_INTRO.conclude}</p>
                  <button className="dialogue-btn" data-testid="darcy-continue-btn" onClick={advanceDarcy}>
                    Addio
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Permanent, discreet Land Editore signature (hidden during dialogues) */}
      {!dObj && !darcyConvo && !showComplete && !showVictory && !confirmRestart && (
        <div className="land-signature" data-testid="land-signature" aria-hidden="true">
          Land Editore
        </div>
      )}

      {/* Non-blocking prompt when the browser needs a fresh gesture for audio */}
      {needAudioTap && !muted && (
        <button
          className="audio-resume"
          data-testid="audio-resume"
          onClick={() => { audio.unlock(); setNeedAudioTap(false); }}
        >
          Tocca per riattivare l'audio
        </button>
      )}

      {/* Optional, non-blocking portrait hint (auto-dismisses) */}
      {showHint && (
        <div className="move-hint" data-testid="orientation-hint">
          Puoi giocare anche in verticale. Per una visuale più ampia, ruota il telefono.
        </div>
      )}
    </div>
  );
}
